import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import events from '../data/events';
import {
  UserGroupIcon,
  CalendarDaysIcon,
  BookOpenIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

// aria-modal tells assistive tech the page behind is inert, but it does not stop Tab from
// walking into it. While a dialog is open: move focus in, keep Tab inside, let Escape close it,
// and hand focus back afterwards (WCAG 2.4.3 Focus Order, 2.1.2 No Keyboard Trap).
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
function useDialogFocus(open, onClose) {
  const ref = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return undefined;
    const box = ref.current;
    const opener = document.activeElement;
    const items = () => (box ? Array.from(box.querySelectorAll(FOCUSABLE)) : []);
    if (box && !box.contains(document.activeElement)) {
      const first = box.querySelector('input:not([disabled])') || items()[0];
      if (first) first.focus();
    }
    const onKey = (e) => {
      if (e.key === 'Escape') { closeRef.current(); return; }
      if (e.key !== 'Tab') return;
      const list = items();
      if (!list.length) return;
      const first = list[0];
      const last = list[list.length - 1];
      const active = document.activeElement;
      if (!box.contains(active)) { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (opener && opener !== document.body && document.contains(opener)) opener.focus();
    };
  }, [open]);
  return ref;
}

const Home = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  
  // Show welcome modal only on first visit
  useEffect(() => {
    // Check if user has seen welcome modal before
    const hasSeenWelcome = localStorage.getItem('hasSeenWelcomeModal');
    
    if (!hasSeenWelcome) {
      const timer = setTimeout(() => {
        setShowWelcomeModal(true);
      }, 2000); // Show after 2 seconds
      return () => clearTimeout(timer);
    }
  }, []);

  const handleModalClose = () => {
    setShowWelcomeModal(false);
    // Mark that user has seen the welcome modal
    localStorage.setItem('hasSeenWelcomeModal', 'true');
  };

  const handleWeeklyUpdatesClick = () => {
    setShowPasswordModal(true);
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    setPasswordError(''); // Clear any previous errors
    
    // Use a simple encoding to avoid exposing the link directly
    const correctPassword = 'testest';
    if (password === correctPassword) {
      // Decode the URL to avoid direct exposure
      const encodedUrl = 'aHR0cHM6Ly9zZWN1cmUuc21vcmUuY29tL24vanV6Nm4tcC1zLTExeC13ZWVrbHktc3RhZmYtY29tbXVuaWNhdGlvbg==';
      const decodedUrl = atob(encodedUrl);
      window.open(decodedUrl, '_blank', 'noopener,noreferrer');
      setShowPasswordModal(false);
      setPassword('');
      setPasswordError('');
    } else {
      setPasswordError('The password you entered is incorrect. Please check your password and try again.');
      setPassword('');
    }
  };

  const handlePasswordModalClose = () => {
    setShowPasswordModal(false);
    setPassword('');
    setPasswordError('');
  };

  const welcomeDialogRef = useDialogFocus(showWelcomeModal, handleModalClose);
  const passwordDialogRef = useDialogFocus(showPasswordModal, handlePasswordModalClose);



  // Condensed timeline for home page
  const timelineItems = [
    { year: "1889", title: "School Founding", description: "P.S. 11X The Highbridge School was established, becoming one of the oldest operating elementary schools in The Bronx." },
    { year: "1981", title: "Historic Landmark", description: "Designated as a New York City Landmark, recognizing our architectural and educational significance." },
    { year: "1996", title: "Early Childhood Building (09X172)", description: "The Early Childhood Building becomes home to our 3K–1st Graders of P.S. 11X." }
  ];

  const getUpcomingEvents = (allEvents) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return allEvents
      .filter((e) => e.date >= today)
      .sort((a, b) => a.date - b.date)
      .slice(0, 3);
  };

  const formatMonth = (date) => date.toLocaleString('default', { month: 'short' }).toUpperCase();
  const formatTimeRange = (event) => {
    if (event.allDay) return 'All Day';
    if (event.startTime && event.endTime) return `${event.startTime} - ${event.endTime}`;
    if (event.startTime) return event.startTime;
    if (event.endTime) return event.endTime;
    return '';
  };

  const upcomingEvents = getUpcomingEvents(events);

  return (
    <main id="main-content" className="bg-white">
      <Navbar />

      {/* Welcome Modal */}
      {showWelcomeModal && (
        <div ref={welcomeDialogRef} className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="welcome-title">
          {/* Transparent Backdrop */}
          <div
            className="fixed inset-0 bg-black/20 backdrop-blur-[2px] transition-all duration-300"
            onClick={handleModalClose}
          ></div>

          {/* Modal */}
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-md bg-white shadow-2xl transform transition-all duration-300 rounded-3xl border border-white/30">
              {/* Close Button */}
              <button
                onClick={handleModalClose}
                aria-label="Close welcome dialog"
                className="absolute top-4 right-4 w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center text-gray-600 hover:text-gray-800 hover:bg-white transition-all duration-200 z-10 shadow-md"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Modal Content */}
              <div className="px-8 py-8">
                {/* Header */}
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center bg-[#630503] text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider mb-4 rounded-full shadow-lg">
                    Welcome
                  </div>
                  <h2 id="welcome-title" className="text-2xl font-light text-gray-900 mb-3 leading-tight">
                    P.S. 11X <span className="font-bold text-[#630503]">The Highbridge School</span>
                  </h2>
                  <div className="w-12 h-px bg-[#630503]/40 mx-auto mb-4"></div>
                  <p className="text-gray-700 leading-relaxed text-sm">
                    Welcome to one of the oldest operating elementary schools in The Bronx. 
                    Excellence in education since 1889.
                  </p>
                </div>

                {/* School Stats */}
                <div className="bg-white/40 backdrop-blur-sm rounded-2xl p-6 mb-6 border border-white/40">
                  <div className="grid grid-cols-2 gap-6 text-center">
                    <div>
                      <div className="text-3xl font-light text-[#630503] mb-1">1889</div>
                      <div className="text-xs text-gray-600 uppercase tracking-wide font-medium">Established</div>
                    </div>
                    <div>
                      <div className="text-3xl font-light text-[#630503] mb-1">3K-5</div>
                      <div className="text-xs text-gray-600 uppercase tracking-wide font-medium">Grades</div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons - Horizontal */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                  <button 
                    onClick={handleModalClose}
                    className="bg-[#630503] hover:bg-[#4a0402] text-white font-semibold py-3 px-3 text-xs uppercase tracking-wide transition-all duration-300 rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                  >
                    Learn More
                  </button>
                  <button 
                    onClick={handleModalClose}
                    className="border-2 border-[#630503]/40 bg-white/50 backdrop-blur-sm text-[#630503] hover:bg-[#630503] hover:text-white hover:border-[#630503] font-semibold py-3 px-3 text-xs uppercase tracking-wide transition-all duration-300 rounded-xl hover:shadow-lg hover:-translate-y-0.5"
                  >
                    Register
                  </button>
                </div>

                {/* Bottom Note */}
                <div className="text-center">
                  <div className="flex items-center justify-center space-x-2 text-xs text-gray-600">
                    <span className="w-1 h-1 bg-[#630503]/60 rounded-full"></span>
                    <span className="font-medium">Highbridge Community</span>
                    <span className="w-1 h-1 bg-[#630503]/60 rounded-full"></span>
                    <span>Bronx, NY</span>
                    <span className="w-1 h-1 bg-[#630503]/60 rounded-full"></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Modal for Weekly Updates */}
      {showPasswordModal && (
        <div ref={passwordDialogRef} className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="password-title">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-all duration-300"
            onClick={handlePasswordModalClose}
          ></div>

          {/* Modal */}
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-md bg-white shadow-2xl transform transition-all duration-300 rounded-2xl">
              {/* Close Button */}
              <button
                onClick={handlePasswordModalClose}
                aria-label="Close password dialog"
                className="absolute top-4 right-4 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center text-gray-600 hover:text-gray-800 transition-all duration-200 z-10"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Modal Content */}
              <div className="px-8 py-8">
                {/* Header */}
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center bg-[#630503] text-white p-3 rounded-full mb-4">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <h2 id="password-title" className="text-2xl font-bold text-gray-900 mb-3">
                    Access Required
                  </h2>
                  <p className="text-gray-600 text-sm">
                    Please enter the password to access weekly updates
                  </p>
                </div>

                                 {/* Password Form */}
                 <form onSubmit={handlePasswordSubmit} className="space-y-6">
                   <div>
                     <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                       Password
                     </label>
                     <input
                       type="password"
                       id="password"
                       value={password}
                       onChange={(e) => {
                         setPassword(e.target.value);
                         if (passwordError) setPasswordError(''); // Clear error when typing
                       }}
                       className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-all duration-200 ${
                         passwordError 
                           ? 'border-red-300 focus:ring-red-500' 
                           : 'border-gray-300 focus:ring-[#630503]'
                       }`}
                       placeholder="Enter password"
                       required
                       autoFocus
                       aria-describedby={passwordError ? "password-error" : undefined}
                       aria-invalid={passwordError ? "true" : "false"}
                     />
                     {passwordError && (
                       <div 
                         id="password-error" 
                         className="mt-2 flex items-start space-x-2 text-sm text-red-600"
                         role="alert"
                         aria-live="polite"
                       >
                         <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                           <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                         </svg>
                         <span>{passwordError}</span>
                       </div>
                     )}
                   </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={handlePasswordModalClose}
                      className="px-6 py-3 border border-gray-300 text-gray-700 hover:bg-gray-50 font-semibold rounded-lg transition-all duration-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-3 bg-[#630503] hover:bg-[#4a0402] text-white font-semibold rounded-lg transition-all duration-200"
                    >
                      Access
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section - Video background with P.S. 11X content */}
      <section className="relative min-h-screen overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0">
          <video 
            autoPlay 
            muted 
            loop 
            playsInline
            className="w-full h-full object-cover"
          >
            <source src="/assets/videos/home/ps11x.mp4" type="video/mp4" />
            {/* Fallback image in case video doesn't load */}
            <img 
              src="/assets/hero-background.jpg" 
              alt="P.S. 11X The Highbridge School" 
              className="w-full h-full object-cover"
            />
          </video>
          {/* Dark overlay for better text readability */}
          <div className="absolute inset-0 bg-black/20"></div>
          {/* Real contrast scrim: sampled the actual video frame-by-frame
              (brightest measured background behind the caption block still
              averaged ~0.64 luma). This flat plate over the bottom ~70% of
              the frame, where the caption always sits, keeps white-on-video
              text at 4.5:1+ even during the brightest moments of the loop —
              not just a decorative fade. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 h-[70%]"
            style={{ backgroundImage: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.55) 85%, rgba(0,0,0,0) 100%)' }}
          ></div>
        </div>

        <div className="relative z-10 h-screen flex">
          {/* Left side content */}
          <div className="flex flex-col justify-end pb-20 pl-8 md:pl-16 lg:pl-24 max-w-3xl">
            {/* School Badge */}
            <div className="inline-block mb-8">
              <span className="bg-white text-[#630503] px-6 py-3 text-sm font-bold uppercase tracking-wider shadow-lg">
                Established 1889
              </span>
            </div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-8 leading-tight drop-shadow-2xl">
              P.S. 11X 
              <span className="block text-3xl md:text-4xl lg:text-5xl text-white font-light mt-3">
                The Highbridge School
              </span>
              <span className="block text-xl md:text-2xl text-white/90 font-normal mt-4">
                One of the oldest operating elementary schools in The Bronx
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-white/95 mb-10 leading-relaxed drop-shadow-lg max-w-2xl">
              Honoring our rich Highbridge history while preparing our students for a future of excellence and innovation.
            </p>
            
            {/* Horizontal line spanning from text to carousel buttons */}
            <div className="relative mb-10 w-full">
              <div className="absolute left-0 h-0.5 bg-white/30" style={{width: 'calc(100vw - 12rem)'}}></div>
            </div>
            
            <div className="flex items-center gap-6">
              <a 
                href="#history" 
                className="bg-[#630503] hover:bg-[#4a0402] text-white px-8 py-4 font-bold text-base transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105"
              >
                <span className="block text-xs opacity-90">NOW</span>
                <span className="block">DISCOVER OUR STORY</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Access Widget Section */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-8">
            {/* Parent Page */}
            <a href="/parents" className="group flex flex-col items-center text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 mb-3 sm:mb-4 bg-[#630503]/10 rounded-full flex items-center justify-center group-hover:bg-[#630503]/20 transition-all duration-300 border border-[#630503]/20">
                <UserGroupIcon className="h-6 w-6 sm:h-9 sm:w-9 text-[#630503]" />
              </div>
              <h2 className="text-xs font-semibold text-gray-900 uppercase tracking-wide">PARENT PAGE</h2>
              <p className="text-xs text-gray-500 mt-1 hidden sm:block">Resources and information for families</p>
            </a>

            {/* Calendar & Events */}
            <a href="/calendar" className="group flex flex-col items-center text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 mb-3 sm:mb-4 bg-[#630503]/10 rounded-full flex items-center justify-center group-hover:bg-[#630503]/20 transition-all duration-300 border border-[#630503]/20">
                <CalendarDaysIcon className="h-6 w-6 sm:h-9 sm:w-9 text-[#630503]" />
              </div>
              <h2 className="text-xs font-semibold text-gray-900 uppercase tracking-wide">CALENDAR & EVENTS</h2>
              <p className="text-xs text-gray-500 mt-1 hidden sm:block">Important dates and school events</p>
            </a>

            {/* Curriculum & Resources */}
            <a href="/learning-resources" className="group flex flex-col items-center text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 mb-3 sm:mb-4 bg-[#630503]/10 rounded-full flex items-center justify-center group-hover:bg-[#630503]/20 transition-all duration-300 border border-[#630503]/20">
                <BookOpenIcon className="h-6 w-6 sm:h-9 sm:w-9 text-[#630503]" />
              </div>
              <h2 className="text-xs font-semibold text-gray-900 uppercase tracking-wide">CURRICULUM & RESOURCES</h2>
              <p className="text-xs text-gray-500 mt-1 hidden sm:block">Academic programs and learning materials</p>
            </a>

            {/* Enrichment Opportunities */}
            <a href="/enrichment" className="group flex flex-col items-center text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 mb-3 sm:mb-4 bg-[#630503]/10 rounded-full flex items-center justify-center group-hover:bg-[#630503]/20 transition-all duration-300 border border-[#630503]/20">
                <SparklesIcon className="h-6 w-6 sm:h-9 sm:w-9 text-[#630503]" />
              </div>
              <h2 className="text-xs font-semibold text-gray-900 uppercase tracking-wide">ENRICHMENT OPPORTUNITIES</h2>
              <p className="text-xs text-gray-500 mt-1 hidden sm:block">Arts, music, library, and special programs</p>
            </a>

            {/* After School Programs */}
            <a href="/after-school" className="group flex flex-col items-center text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 mb-3 sm:mb-4 bg-[#630503]/10 rounded-full flex items-center justify-center group-hover:bg-[#630503]/20 transition-all duration-300 border border-[#630503]/20">
                <UserGroupIcon className="h-6 w-6 sm:h-9 sm:w-9 text-[#630503]" />
              </div>
              <h2 className="text-xs font-semibold text-gray-900 uppercase tracking-wide">AFTER SCHOOL</h2>
              <p className="text-xs text-gray-500 mt-1 hidden sm:block">Community programs with Mosaic and ACDP</p>
            </a>
          </div>
        </div>
      </section>

      {/* History Section - Modern Grid Layout */}
      <section id="history" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Left Column: Text Content */}
            <div className="lg:col-span-5 flex flex-col justify-center">
              <div className="mb-8">
                <span className="inline-block bg-[#630503] text-white px-6 py-3 text-sm font-bold uppercase tracking-wider shadow-lg">
                  Since 1889
                </span>
              </div>
              
              <h2 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-8 leading-tight">
                Our Historic Legacy
              </h2>
              
              <p className="text-xl text-gray-600 leading-relaxed mb-10 max-w-lg">
                Founded in 1889, P.S. 11X The Highbridge School stands proudly as one of the oldest operating elementary schools in the Bronx. For over 130 years, we've been a cornerstone of education in New York City, nurturing generations of learners.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Link to="/history#timeline" className="bg-[#630503] hover:bg-[#4a0402] text-white font-bold px-8 py-4 text-sm uppercase tracking-wide transition-all duration-300 shadow-lg hover:shadow-xl inline-block">
                  View Timeline
                </Link>
              </div>
            </div>

            {/* Right Column: Image Grid */}
            <div className="lg:col-span-7">
              <div className="grid grid-cols-2 gap-6 h-[550px]">
                {/* Large Image */}
                <div className="col-span-2 bg-gray-200 rounded-xl overflow-hidden shadow-lg">
                  <img 
                    src="/assets/images/home/main.jpg" 
                    alt="Historic P.S. 11X Building" 
                    className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                </div>
                
                {/* Two smaller images */}
                <div className="bg-gray-200 rounded-xl overflow-hidden shadow-lg">
                  <img 
                    src="/assets/images/home/satellite.jpg" 
                    alt="School Heritage" 
                    className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                </div>
                <div className="bg-[#630503] rounded-xl flex items-center justify-center shadow-lg">
                  <div className="text-center text-white p-8">
                    <div className="text-4xl font-bold mb-3">130+</div>
                    <div className="text-sm uppercase tracking-wider font-semibold">Years of Excellence</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section - Clean Timeline Design */}
      <section id="timeline" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-20">
            <span className="inline-block bg-[#630503] text-white px-6 py-3 text-sm font-bold uppercase tracking-wider mb-8 shadow-lg">
              Our History
            </span>
            <h2 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-8 leading-tight">
              Our Journey Through Time
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Key milestones in the history of P.S. 11X, marking our evolution from a small neighborhood school to a cornerstone of Bronx education.
            </p>
          </div>
          
          {/* Improved Timeline Design */}
          <div className="relative">
            {/* Central Timeline Line - Desktop */}
            <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-[#630503] via-[#8B4513] to-[#A0522D] transform -translate-x-1/2"></div>
            
            {/* Timeline Items */}
            <div className="space-y-16 lg:space-y-24">
              {timelineItems.map((item, index) => (
                <div key={index} className="relative">
                  {/* Mobile/Tablet Layout */}
                  <div className="lg:hidden">
                    <div className="flex items-start">
                      {/* Timeline Dot */}
                      <div className="flex-shrink-0 w-12 h-12 bg-[#630503] rounded-full flex items-center justify-center mr-6 mt-2">
                        <div className="w-4 h-4 bg-white rounded-full"></div>
                      </div>
                      {/* Content */}
                      <div className="flex-1 bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                        <div className="inline-block bg-[#630503] text-white px-4 py-2 text-sm font-bold mb-6 rounded-lg">
                          {item.year}
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-4">{item.title}</h3>
                        <p className="text-gray-600 leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                    {/* Connecting Line */}
                    {index < timelineItems.length - 1 && (
                      <div className="w-1 h-16 bg-[#630503]/30 ml-6 mt-4"></div>
                    )}
                  </div>

                  {/* Desktop Layout */}
                  <div className="hidden lg:block">
                    <div className={`flex items-center ${index % 2 === 0 ? 'justify-start' : 'justify-end'}`}>
                      <div className={`w-5/12 ${index % 2 === 0 ? 'pr-12' : 'pl-12'}`}>
                        <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 relative">
                          {/* Timeline Connector to Center Line */}
                          <div className={`absolute top-1/2 ${index % 2 === 0 ? '-right-14' : '-left-14'} w-12 h-0.5 bg-[#630503] transform -translate-y-1/2`}></div>
                          
                          <div className="inline-block bg-[#630503] text-white px-5 py-3 text-sm font-bold mb-6 rounded-lg shadow-md">
                            {item.year}
                          </div>
                          <h3 className="text-2xl font-bold text-gray-900 mb-5">{item.title}</h3>
                          <p className="text-gray-600 leading-relaxed text-lg">{item.description}</p>
                          
                          {/* Visual accent */}
                          <div className="mt-6 w-16 h-1 bg-gradient-to-r from-[#630503] to-gray-200 rounded-full"></div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Timeline Dot on Center Line */}
                    <div className="absolute left-1/2 transform -translate-x-1/2 w-6 h-6 bg-white border-4 border-[#630503] rounded-full shadow-lg" style={{top: `${index * 24 + 12}rem`}}></div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Timeline End Marker */}
            <div className="flex justify-center mt-20">
              <div className="relative">
                {/* End Dot */}
                <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 -top-10 w-8 h-8 bg-[#630503] rounded-full shadow-lg"></div>
                <Link 
                  to="/history#timeline" 
                  className="bg-[#630503] hover:bg-[#4a0402] text-white font-bold px-10 py-5 text-lg uppercase tracking-wide transition-all duration-300 shadow-xl hover:shadow-2xl rounded-lg inline-block"
                >
                  Continuing Our Legacy
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* School Welcome & Philosophy Section */}
      <section className="py-24 bg-[#630503] relative overflow-hidden">
        {/* Enhanced Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-16 w-64 h-64 border border-white/10 rounded-full"></div>
          <div className="absolute bottom-32 right-20 w-48 h-48 border border-white/10 rounded-full"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 border border-white/5 rounded-full"></div>
          <div className="absolute top-1/3 right-1/4 w-2 h-2 bg-white/20 rounded-full"></div>
          <div className="absolute bottom-1/4 left-1/3 w-3 h-3 bg-white/15 rounded-full"></div>
          <div className="absolute top-3/4 left-1/5 w-1 h-1 bg-white/25 rounded-full"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            {/* Header Section */}
            <div className="mb-16">
              <span className="inline-block bg-white/10 backdrop-blur-sm text-white px-6 py-3 text-sm font-medium uppercase tracking-[0.2em] mb-8 rounded-full">
                Welcome to Our School
              </span>
              <h2 className="text-4xl lg:text-5xl xl:text-6xl font-light text-white mb-8 leading-[1.1] max-w-4xl mx-auto">
                The Home of <span className="font-bold">Bronx Brilliance</span>
              </h2>
              <div className="w-24 h-px bg-white/40 mx-auto"></div>
            </div>
            
            {/* Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
              {/* Left Column - Main Content */}
              <div className="lg:col-span-8 text-left">
                <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 lg:p-12 mb-8">
                  <p className="text-lg lg:text-xl leading-relaxed text-white/95 mb-8">
                    Welcome to P.S.011x: The Highbridge School, the home of Bronx Brilliance. Built in 1889, we believe we are the oldest operating elementary school in The Bronx, which makes us the number one school in the Bronx! Located in the heart of the historic Highbridge community, P.S.011x is a collaborative community. Our Early Childhood Satellite school with 3K-1st Grade and the Main Building consists of Grades 2-5.
                  </p>
                  
                  <div className="flex items-center mb-8">
                    <div className="w-12 h-px bg-white/30"></div>
                    <div className="w-1 h-1 bg-white/50 rounded-full mx-4"></div>
                    <div className="w-12 h-px bg-white/30"></div>
                  </div>
                  
                  <p className="text-lg lg:text-xl leading-relaxed text-white/95 mb-8">
                    We believe that every single person has gifts and talents. What if school became the place where children and adults explore and recognize their gifts and talents? What would school look and feel like? Come to P.S.011x- discover your brilliance and then find the courage to share your brilliance with the world.
                  </p>
                  
                  <p className="text-lg lg:text-xl leading-relaxed text-white/95">
                    P.S. 011x: Courage, Creativity, and Collaboration- There is Unity in our community. Come and visit us and feel the love for yourself.
                  </p>
                </div>
              </div>
              
              {/* Right Column - Values & CTA */}
              <div className="lg:col-span-4 space-y-8">
                {/* Core Values Card */}
                <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8">
                  <h3 className="text-2xl font-semibold text-white mb-6 text-center">Our Values</h3>
                  <div className="space-y-4">
                    <div className="flex items-center text-white/90">
                      <div className="w-2 h-2 bg-white/60 rounded-full mr-4"></div>
                      <span className="text-lg">Courage</span>
                    </div>
                    <div className="flex items-center text-white/90">
                      <div className="w-2 h-2 bg-white/60 rounded-full mr-4"></div>
                      <span className="text-lg">Creativity</span>
                    </div>
                    <div className="flex items-center text-white/90">
                      <div className="w-2 h-2 bg-white/60 rounded-full mr-4"></div>
                      <span className="text-lg">Collaboration</span>
                    </div>
                  </div>
                </div>
                
                {/* Established Badge */}
                <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 text-center">
                  <div className="text-4xl font-bold text-white mb-2">1889</div>
                  <div className="text-white/80 text-sm uppercase tracking-wider">Established</div>
                  <div className="w-16 h-px bg-white/40 mx-auto mt-4"></div>
                  <div className="text-white/90 text-sm mt-4">130+ Years of Excellence</div>
                </div>

                {/* District 9 Logo */}
                <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 text-center">
                  <img
                    src="/assets/images/home/FINAL D9 LOGO RED.png"
                    alt="Community School District 9"
                    className="h-20 w-auto mx-auto mb-3"
                  />
                  <div className="text-white/80 text-sm uppercase tracking-wider">Community School District 9</div>
                </div>
              </div>
            </div>
            
            {/* You Belong Here Section */}
            <div className="mt-20 relative">
              <div className="bg-white/10 backdrop-blur-sm rounded-3xl py-16 px-8 relative overflow-hidden">
                <div className="relative z-10">
                  <h3 className="text-3xl lg:text-4xl xl:text-5xl font-light text-white text-center tracking-wide mb-8">
                    YOU <span className="font-bold">BELONG</span> HERE
                  </h3>
                  <div className="w-32 h-px bg-white/50 mx-auto mb-12"></div>
                  
                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-6 justify-center">
                    <button
                      onClick={handleWeeklyUpdatesClick}
                      className="bg-white text-[#630503] hover:bg-white/90 font-semibold px-12 py-4 text-base tracking-wide transition-all duration-300 hover:shadow-xl rounded-lg"
                    >
                      Weekly Updates
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Community Showcase - Modern Grid Layout */}
      <section id="community" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="mb-20">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
              <div className="lg:col-span-8">
                <span className="inline-block bg-[#630503] text-white px-6 py-3 text-sm font-bold uppercase tracking-wider mb-8 shadow-lg">
              Community Spotlight
                </span>
                <h2 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 leading-tight">
                  Heroes of Highbridge
                </h2>
              </div>
              <div className="lg:col-span-4">
                <p className="text-xl text-gray-600 leading-relaxed">
                  Meet the remarkable people who make P.S. 11X special. Our students, teachers, and families create a strong, supportive community.
                </p>
              </div>
            </div>
          </div>

          {/* Community Tabs */}
          <div className="mb-16">
            <div className="flex flex-wrap gap-3" role="tablist" aria-label="Community showcase">
                <button
                role="tab"
                aria-selected={activeSlide === 0}
                aria-controls="tabpanel-students"
                className={`px-8 py-4 text-sm font-bold uppercase tracking-wide transition-all duration-300 shadow-lg ${
                    activeSlide === 0
                    ? 'bg-[#630503] text-white shadow-xl'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:shadow-md'
                  }`}
                  onClick={() => setActiveSlide(0)}
                >
                  Student Spotlights
                </button>
                <button
                role="tab"
                aria-selected={activeSlide === 1}
                aria-controls="tabpanel-staff"
                className={`px-8 py-4 text-sm font-bold uppercase tracking-wide transition-all duration-300 shadow-lg ${
                    activeSlide === 1
                    ? 'bg-[#630503] text-white shadow-xl'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:shadow-md'
                  }`}
                  onClick={() => setActiveSlide(1)}
                >
                  Staff Highlights
                </button>
                <button
                role="tab"
                aria-selected={activeSlide === 2}
                aria-controls="tabpanel-parents"
                className={`px-8 py-4 text-sm font-bold uppercase tracking-wide transition-all duration-300 shadow-lg ${
                    activeSlide === 2
                    ? 'bg-[#630503] text-white shadow-xl'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:shadow-md'
                  }`}
                  onClick={() => setActiveSlide(2)}
                >
                  Parent Testimonials
                </button>
            </div>
          </div>

          {/* Student Spotlights */}
          <div id="tabpanel-students" role="tabpanel" className={`${activeSlide === 0 ? 'block' : 'hidden'}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
              {/* Student 1 - Large Feature */}
              <div className="md:col-span-2 lg:col-span-2 bg-gray-50 rounded-xl overflow-hidden shadow-lg">
                <div className="grid grid-cols-1 lg:grid-cols-2 h-full">
                  <div className="bg-gray-200">
                  <img 
                    src="https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" 
                    alt="Student Art Showcase" 
                      className="w-full h-full object-cover"
                  />
                  </div>
                  <div className="p-10 flex flex-col justify-center">
                    <span className="inline-block bg-blue-600 text-white px-4 py-2 text-xs font-bold uppercase tracking-wide mb-6 rounded-lg shadow-md">
                      Arts Program
                    </span>
                    <h3 className="text-3xl font-bold text-gray-900 mb-3">Maria Rodriguez</h3>
                    <p className="text-sm text-gray-500 mb-6 font-medium">5th Grade • Visual Arts</p>
                    <p className="text-gray-600 leading-relaxed text-lg">
                      Maria's artwork was selected for display at the Bronx Museum of the Arts' annual student showcase.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Student 2 - Compact */}
              <div className="bg-gray-50 rounded-xl p-8 shadow-lg">
                <span className="inline-block bg-emerald-700 text-white px-4 py-2 text-xs font-bold uppercase tracking-wide mb-6 rounded-lg shadow-md">
                      Math Team
                </span>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">James Washington</h3>
                <p className="text-sm text-gray-500 mb-6 font-medium">4th Grade • Mathematics</p>
                <p className="text-gray-600 leading-relaxed">
                  James led our math team to victory at the Bronx Math Olympics, bringing home the championship trophy.
                  </p>
                </div>
              
              {/* Student 3 - Compact */}
              <div className="bg-gray-50 rounded-xl p-8 shadow-lg">
                <span className="inline-block bg-purple-600 text-white px-4 py-2 text-xs font-bold uppercase tracking-wide mb-6 rounded-lg shadow-md">
                  Leadership
                </span>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Aisha Johnson</h3>
                <p className="text-sm text-gray-500 mb-6 font-medium">3rd Grade • Community Service</p>
                <p className="text-gray-600 leading-relaxed">
                  Aisha organized a school-wide donation drive, collecting over 500 items for our local food pantry.
                </p>
              </div>
              
              {/* Stats Block */}
              <div className="md:col-span-2 bg-[#630503] rounded-xl p-12 text-white shadow-lg">
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold mb-2">Our School by the Numbers</h3>
                  <p className="text-white/80 text-sm">Building excellence together</p>
                </div>
                <div className="grid grid-cols-3 gap-12">
                  <div className="text-center">
                    <div className="text-5xl font-bold mb-4 text-white">450+</div>
                    <div className="text-sm uppercase tracking-wider font-semibold text-white/90">Students</div>
                    <div className="w-12 h-1 bg-white/30 mx-auto mt-3 rounded-full"></div>
                  </div>
                  <div className="text-center">
                    <div className="text-5xl font-bold mb-4 text-white">25+</div>
                    <div className="text-sm uppercase tracking-wider font-semibold text-white/90">Teachers</div>
                    <div className="w-12 h-1 bg-white/30 mx-auto mt-3 rounded-full"></div>
                  </div>
                  <div className="text-center">
                    <div className="text-5xl font-bold mb-4 text-white">95%</div>
                    <div className="text-sm uppercase tracking-wider font-semibold text-white/90">Satisfaction</div>
                    <div className="w-12 h-1 bg-white/30 mx-auto mt-3 rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Teacher Highlights */}
          <div id="tabpanel-staff" role="tabpanel" className={`${activeSlide === 1 ? 'block' : 'hidden'}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
              {[
                { name: "Ms. Patel", subject: "Science", years: "20 Years", description: "Innovative science curriculum integrating neighborhood ecology." },
                { name: "Mr. Gonzalez", subject: "Music", years: "15 Years", description: "Revitalized music program with classical and Bronx traditions." },
                { name: "Ms. Jackson", subject: "History", years: "8 Years", description: "Created popular 'Bronx Heritage' curriculum connecting students to community." }
              ].map((teacher, index) => (
                <div key={index} className="bg-gray-50 rounded-xl p-8 shadow-lg">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{teacher.name}</h3>
                  <p className="text-sm text-gray-500 mb-6 font-medium">{teacher.subject} • {teacher.years}</p>
                  <p className="text-gray-600 leading-relaxed">
                    {teacher.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Parent Testimonials */}
          <div id="tabpanel-parents" role="tabpanel" className={`${activeSlide === 2 ? 'block' : 'hidden'}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div className="bg-gray-50 rounded-xl p-10 shadow-lg">
                <p className="text-gray-600 leading-relaxed mb-8 italic text-lg">
                  "The teachers at P.S. 11X have created an environment where my daughter feels both challenged and supported."
                  </p>
                  <div className="flex items-center">
                  <div className="w-16 h-16 bg-gray-300 rounded-full mr-6"></div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-lg">Sarah Martinez</h4>
                    <p className="text-sm text-gray-500 font-medium">Parent of 4th Grader</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-xl p-10 shadow-lg">
                <p className="text-gray-600 leading-relaxed mb-8 italic text-lg">
                  "As a P.S. 11X alumnus myself, I'm thrilled that my son now attends the same school."
                  </p>
                  <div className="flex items-center">
                  <div className="w-16 h-16 bg-gray-300 rounded-full mr-6"></div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-lg">Michael Thompson</h4>
                    <p className="text-sm text-gray-500 font-medium">Parent of 2nd Grader, P.S. 11X Class of '95</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Events - Modern Grid Layout */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <span className="inline-block bg-[#630503] text-white px-6 py-3 text-sm font-bold uppercase tracking-wider mb-8 shadow-lg">
              Stay Connected
            </span>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-8 leading-tight">
              Events
            </h2>
          </div>
          
          <div className="flex justify-center">
            {/* Upcoming Events */}
            <div className="bg-white rounded-xl p-10 shadow-lg max-w-2xl w-full">
              <div className="flex items-center mb-10">
                <CalendarDaysIcon className="h-10 w-10 text-[#630503] mr-4" />
                <h3 className="text-3xl font-bold text-gray-900">Upcoming Events</h3>
              </div>
              
              <div className="space-y-8">
                {upcomingEvents.length > 0 ? (
                  upcomingEvents.map((event) => (
                    <div key={event.id} className="flex items-start p-6 bg-gray-50 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300">
                      <div className="flex-shrink-0 w-20 h-20 bg-[#630503] text-white rounded-xl flex flex-col items-center justify-center mr-6 shadow-md">
                        <span className="text-xs font-bold uppercase">{formatMonth(event.date)}</span>
                        <span className="text-xl font-bold">{event.date.getDate()}</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900 mb-2 text-lg">{event.title}</h4>
                        <p className="text-gray-600">{formatTimeRange(event)}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-600">No upcoming events.</p>
                )}
              </div>
              
              <Link to="/calendar" className="mt-10 block text-center w-full bg-[#630503] hover:bg-[#4a0402] text-white font-bold px-8 py-4 text-sm uppercase tracking-wide transition-all duration-300 shadow-lg hover:shadow-xl">
                View Full Calendar
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default Home; 